import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, Download, Check, ShieldAlert, Clock } from 'lucide-react';
import { functions } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { useCompanySettings, useAdminSettings } from '../../hooks/useCompanyData';
import { useAdminActiveTasks } from '../../hooks/useAdminTasks';
import Modal from '../../components/common/Modal';
import { downloadAccountBackup } from '../../utils/accountBackup';
import './AccountResetSection.css';

const CONFIRM_PHRASE = 'RESET MY ACCOUNT';

const resetCallable = httpsCallable(functions, 'resetAccountData');

export default function AccountResetSection() {
  const { t } = useTranslation();
  const { companyId, isAdmin } = useAuth();
  const { settings } = useCompanySettings();
  const { adminSettings } = useAdminSettings();
  const { tasks: activeTasks } = useAdminActiveTasks();
  const toast = useToast();
  const navigate = useNavigate();

  const [backingUp, setBackingUp] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [phrase, setPhrase] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  if (!isAdmin) return null;

  const companyName = settings?.companyName || '';
  const currency = adminSettings?.labourCostCurrency || 'CAD';
  const activeTimerCount = activeTasks?.length ?? 0;

  const phraseMatches = phrase.trim() === CONFIRM_PHRASE;
  const canReset = !!backupInfo && phraseMatches && !resetting;

  const handleBackup = async () => {
    if (backingUp) return;
    setBackingUp(true);
    try {
      const result = await downloadAccountBackup({ companyId, companyName, currency });
      setBackupInfo({ filename: result.filename, generatedAt: result.generatedAt });
      toast(t('reset.backupDownloaded'), 'success');
    } catch (err) {
      console.error('[Floora] account backup failed:', err);
      toast(t('reset.backupFailed'), 'error');
    } finally {
      setBackingUp(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!canReset) return;
    setResetting(true);
    try {
      await resetCallable({
        confirmationPhrase: phrase.trim(),
        backupRef: backupInfo.filename,
        backupGeneratedAt: backupInfo.generatedAt.getTime(),
      });
      setShowModal(false);
      setPhrase('');
      setBackupInfo(null);
      toast(t('reset.resetComplete'), 'success');
      navigate('/admin');
    } catch (err) {
      console.error('[Floora] account reset failed:', err);
      const code = err?.code || '';
      let msg = t('reset.resetFailed');
      if (code === 'functions/permission-denied') msg = t('reset.errPermission');
      else if (code === 'functions/invalid-argument') msg = t('reset.errPhrase');
      else if (code === 'functions/unauthenticated') msg = t('reset.errAuth');
      else if (code === 'functions/internal') msg = err.message || msg;
      toast(msg, 'error');
      setShowModal(false);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="card danger-zone">
      <div className="card-header danger-zone__header">
        <ShieldAlert size={18} />
        <span className="card-title">{t('reset.title')}</span>
      </div>

      <div className="card-body danger-zone__body">
        <p className="danger-zone__lead">{t('reset.lead')}</p>

        <div className="danger-zone__meta">
          <span className="danger-zone__meta-del">
            <strong>{t('reset.deletedTitle')}:</strong>{' '}
            {t('reset.delTasks')}, {t('reset.delTimers')}, {t('reset.delProduction')},
            {' '}{t('reset.delAnalytics')}, {t('reset.delCosts')}, {t('reset.delLogs')}.
          </span>
          <span className="danger-zone__meta-keep">
            <strong>{t('reset.keptTitle')}:</strong>{' '}
            {t('reset.keepUsers')}, {t('reset.keepRoles')}, {t('reset.keepAccess')},
            {' '}{t('reset.keepRates')}, {t('reset.keepSettings')}.
          </span>
        </div>

        {activeTimerCount > 0 && (
          <div className="danger-zone__warn">
            <Clock size={14} />
            <span>{t('reset.activeTimerWarn', { count: activeTimerCount })}</span>
          </div>
        )}

        {/* Backup */}
        <div className="danger-zone__row">
          <button className="btn btn-secondary" onClick={handleBackup} disabled={backingUp}>
            {backingUp ? <span className="spinner" /> : <><Download size={14} />{t('reset.downloadBackup')}</>}
          </button>
          {backupInfo && (
            <span className="danger-zone__backup-ok">
              <Check size={14} />
              {t('reset.backupDownloaded')}
            </span>
          )}
        </div>

        {/* Confirmation phrase */}
        <div className={`danger-zone__phrase-row${!backupInfo ? ' danger-zone__phrase-row--locked' : ''}`}>
          <label className="danger-zone__phrase-label">
            {t('reset.step2Hint')}{' '}
            <code className="danger-zone__phrase-code">{CONFIRM_PHRASE}</code>
          </label>
          <input
            className="form-control danger-zone__phrase-input"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            disabled={!backupInfo || resetting}
            aria-label={t('reset.step2Title')}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {/* Reset action */}
        <div className="danger-zone__action">
          <button
            className="btn btn-danger"
            disabled={!canReset}
            onClick={() => setShowModal(true)}
          >
            <AlertTriangle size={14} />
            {t('reset.resetButton')}
          </button>
          {!backupInfo && (
            <span className="form-hint">{t('reset.disabledHintNoBackup')}</span>
          )}
          {backupInfo && !phraseMatches && (
            <span className="form-hint">{t('reset.disabledHintNoPhrase')}</span>
          )}
        </div>
      </div>

      {showModal && (
        <Modal
          title={t('reset.modalTitle')}
          onClose={() => !resetting && setShowModal(false)}
          size="sm"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={resetting}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-danger" onClick={handleConfirmReset} disabled={resetting}>
                {resetting ? <span className="spinner" /> : t('reset.modalConfirm')}
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <p style={{ color: 'var(--text-secondary)' }}>{t('reset.modalBody')}</p>
            {activeTimerCount > 0 && (
              <div className="danger-zone__warn">
                <Clock size={14} />
                <span>{t('reset.activeTimerWarn', { count: activeTimerCount })}</span>
              </div>
            )}
            <p className="danger-zone__irreversible">
              <AlertTriangle size={14} />
              {t('reset.modalIrreversible')}
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
